import { makeAutoObservable } from 'mobx';
import type { IClock } from './Clock';
import type { ILogger } from './Logger';

// "Is the gateway answering?", as a small observable a screen can gate on. `navigator.onLine` says
// the machine has a network interface; it does not say the gateway is up, and a desk on hall wifi
// with a dead gateway would otherwise keep its buttons enabled and find out one failed write at a
// time (session/scraps.md, the P2 defer).
//
// CLEAR/LATCH SEMANTICS, decided here and worth reading before changing anything:
//
//   The signal is the SDK's `status.lastError`, and it does NOT latch. The engine clears it on the
//   next successful `execute()` or `refresh()` and sets it only on a conflict or a network throw
//   (a ValidationError, an UpdateRequiredError or an OfflineError never touch it). So a transient
//   failure closes the gate and the next thing that works re-opens it.
//
//   That is only half the answer, because a CLOSED gate stops the writes that would clear it: a
//   desk whose buttons are disabled makes no further attempts, and nothing else pokes an
//   unreachable gateway either. So an unreachable gateway starts a probe here — one cheap
//   write-free catch-up read every PROBE_MS — and the probe's own success is what clears the
//   error. That is the difference between "the desk recovers on its own in five seconds" and "the
//   desk is disabled for the rest of the hour".
//
// The probe stops the moment the gateway answers, so a healthy device runs no timer at all.

// The one field of the SDK's SyncStatus this cares about, typed structurally so nothing here has an
// SDK edge (INVARIANTS rule 22).
export interface ReachStatus {
  readonly lastError: string | undefined;
}

const PROBE_MS = 5_000;

export class GatewayReach {
  private _reachable = true;
  private stopProbe: (() => void) | undefined;

  constructor(
    private readonly clock: IClock,
    private readonly logger: ILogger,
    // A cheap, write-free read that resolves when the gateway answers. Rejections are expected and
    // swallowed: an unreachable gateway is the state this is trying to leave, not an app failure.
    private readonly probe: () => Promise<void>,
  ) {
    makeAutoObservable<GatewayReach, 'clock' | 'logger' | 'probe' | 'stopProbe'>(this, {
      clock: false,
      logger: false,
      probe: false,
      stopProbe: false,
    });
  }

  get reachable(): boolean {
    return this._reachable;
  }

  // Fold one status emission. Called from the handle's status subscription and once with
  // `handle.status` at open, because subscribeStatus does not replay the current value.
  apply(status: ReachStatus): void {
    this.set(status.lastError === undefined);
  }

  // Back to the optimistic default with no timer running. Called when the handle is torn down: a
  // closed handle's reachability is nobody's question.
  reset(): void {
    this.set(true);
  }

  private set(reachable: boolean): void {
    if (reachable === this._reachable) return;
    this._reachable = reachable;
    if (reachable) {
      this.logger.info('gateway reachable again');
      this.stopProbe?.();
      this.stopProbe = undefined;
      return;
    }
    this.logger.warn('gateway unreachable, probing');
    this.stopProbe ??= this.clock.startTicker(PROBE_MS, this.runProbe);
  }

  // Fire and forget on purpose: the outcome arrives as a status emission through `apply`, not as a
  // return value. A rejection here is the unreachable gateway saying so again.
  private runProbe = (): void => {
    void this.probe().catch(() => {
      this.logger.debug('gateway probe failed, still unreachable');
    });
  };
}
